import React, { useState, useEffect, useRef } from 'react';

const PRODUCT_TYPES = [
  { key: 'sets',               label: 'Sets' },
  { key: 'wildmicr',          label: 'Wildflowers & Microgreens' },
  { key: 'individualpackets', label: 'Individual Packets' },
  { key: 'fert_products',     label: 'Fertilizers' },
  { key: 'readyproducts',     label: 'Ready Products' },
  { key: 'kit',               label: 'Kits' },
];

// Format timestamp as "Apr 14, 15:30"
const fmtTs = (ts) => {
  if (!ts) return null;
  const d = new Date(ts);
  if (isNaN(d)) return null;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
    ' ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
};

const ShipmentsToolbar = ({
  searchQuery, setSearchQuery,
  selectedBrands, setSelectedBrands,
  selectedNiches, setSelectedNiches,
  selectedTypes, setSelectedTypes,
  brands, niches,
  filteredCount, totalCount,
  hasActiveFilters, clearFilters,
  shipDate, setShipDate,
  onOpenFbaPopup, onOpenAwdPopup,
  fbaUpdatedAt, awdUpdatedAt,
  minBoxesFilter, setMinBoxesFilter,
  showShipmentOnly, setShowShipmentOnly,
  showOosOnly, setShowOosOnly,
  sizeFilter, setSizeFilter, packSizes = [],
  onExportAmazonUpload,
  onOpenSkuMapping,
  stockButtonLabel = 'FBA Stock',
  showAwd = true,
  showExport = true,
  showSkuMapping = true,
}) => {
  const [brandOpen, setBrandOpen] = useState(false);
  const [nicheOpen, setNicheOpen] = useState(false);
  const [typeOpen,  setTypeOpen]  = useState(false);
  const [boxesOpen, setBoxesOpen] = useState(false);
  const brandRef = useRef(null);
  const nicheRef = useRef(null);
  const typeRef  = useRef(null);
  const boxesRef = useRef(null);

  useEffect(() => {
    const handleOut = (e) => {
      if (brandRef.current && !brandRef.current.contains(e.target)) setBrandOpen(false);
      if (nicheRef.current && !nicheRef.current.contains(e.target)) setNicheOpen(false);
      if (typeRef.current  && !typeRef.current.contains(e.target))  setTypeOpen(false);
      if (boxesRef.current && !boxesRef.current.contains(e.target)) setBoxesOpen(false);
    };
    document.addEventListener('mousedown', handleOut);
    return () => document.removeEventListener('mousedown', handleOut);
  }, []);

  const toggleBrand = (id) => {
    setSelectedBrands(prev => prev.includes(id) ? prev.filter(b => b !== id) : [...prev, id]);
  };

  const toggleNiche = (id) => {
    setSelectedNiches(prev => prev.includes(id) ? prev.filter(n => n !== id) : [...prev, id]);
  };

  const toggleType = (key) => {
    setSelectedTypes(prev => prev.includes(key) ? prev.filter(t => t !== key) : [...prev, key]);
  };

  return (
    <div className="sp-toolbar">
      {/* Ship Date */}
      <div className="sp-shipdate-wrapper">
        <label className="sp-shipdate-label">
          Ship Date:
        </label>
        <input
          type="date"
          className="sp-shipdate-input"
          value={shipDate || ''}
          onChange={e => setShipDate(e.target.value)}
        />
      </div>

      <div className="sp-toolbar-divider" />

      {/* Search */}
      <div className="sp-search-wrapper">
        <i className="fas fa-search sp-search-icon"></i>
        <input
          type="text"
          className="sp-search-input"
          placeholder="Search by name, SKU or ASIN…"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
        {searchQuery && (
          <button className="sp-search-clear" onClick={() => setSearchQuery('')}>
            <i className="fas fa-times"></i>
          </button>
        )}
      </div>

      {/* Product Type filter */}
      <div className="sp-filter-wrapper" ref={typeRef}>
        <button
          className={`sp-filter-btn${selectedTypes.length > 0 ? ' active' : ''}`}
          onClick={() => { setTypeOpen(o => !o); setBrandOpen(false); setNicheOpen(false); }}
        >
          <i className="fas fa-cubes"></i>
          Type
          {selectedTypes.length > 0 && <span className="sp-filter-badge">{selectedTypes.length}</span>}
          <i className={`fas fa-chevron-${typeOpen ? 'up' : 'down'} sp-chevron`}></i>
        </button>
        {typeOpen && (
          <div className="sp-dropdown">
            <div className="sp-dropdown-header">Filter by Type</div>
            {PRODUCT_TYPES.map(t => (
              <label key={t.key} className="sp-dropdown-option">
                <input
                  type="checkbox"
                  checked={selectedTypes.includes(t.key)}
                  onChange={() => toggleType(t.key)}
                />
                {t.label}
              </label>
            ))}
            {selectedTypes.length > 0 && (
              <button className="sp-dropdown-clear" onClick={() => setSelectedTypes([])}>Clear</button>
            )}
          </div>
        )}
      </div>

      {/* Brand filter */}
      <div className="sp-filter-wrapper" ref={brandRef}>
        <button
          className={`sp-filter-btn${selectedBrands.length > 0 ? ' active' : ''}`}
          onClick={() => { setBrandOpen(o => !o); setNicheOpen(false); setTypeOpen(false); }}
        >
          <i className="fas fa-tag"></i>
          Brand
          {selectedBrands.length > 0 && <span className="sp-filter-badge">{selectedBrands.length}</span>}
          <i className={`fas fa-chevron-${brandOpen ? 'up' : 'down'} sp-chevron`}></i>
        </button>
        {brandOpen && (
          <div className="sp-dropdown">
            <div className="sp-dropdown-header">Filter by Brand</div>
            {brands.length === 0 ? (
              <div className="sp-dropdown-empty">No brands</div>
            ) : brands.map(b => (
              <label key={b.brandID} className="sp-dropdown-option">
                <input
                  type="checkbox"
                  checked={selectedBrands.includes(String(b.brandID))}
                  onChange={() => toggleBrand(String(b.brandID))}
                />
                {b.brandName || b.brandID}
              </label>
            ))}
            {selectedBrands.length > 0 && (
              <button className="sp-dropdown-clear" onClick={() => setSelectedBrands([])}>Clear</button>
            )}
          </div>
        )}
      </div>

      {/* Niche filter */}
      <div className="sp-filter-wrapper" ref={nicheRef}>
        <button
          className={`sp-filter-btn${selectedNiches.length > 0 ? ' active' : ''}`}
          onClick={() => { setNicheOpen(o => !o); setBrandOpen(false); setTypeOpen(false); }}
        >
          <i className="fas fa-layer-group"></i>
          Niche
          {selectedNiches.length > 0 && <span className="sp-filter-badge">{selectedNiches.length}</span>}
          <i className={`fas fa-chevron-${nicheOpen ? 'up' : 'down'} sp-chevron`}></i>
        </button>
        {nicheOpen && (
          <div className="sp-dropdown">
            <div className="sp-dropdown-header">Filter by Niche</div>
            {niches.length === 0 ? (
              <div className="sp-dropdown-empty">No niches</div>
            ) : niches.map(n => (
              <label key={n.niche_category_id} className="sp-dropdown-option">
                <input
                  type="checkbox"
                  checked={selectedNiches.includes(String(n.niche_category_id))}
                  onChange={() => toggleNiche(String(n.niche_category_id))}
                />
                {n.niche_name}
              </label>
            ))}
            {selectedNiches.length > 0 && (
              <button className="sp-dropdown-clear" onClick={() => setSelectedNiches([])}>Clear</button>
            )}
          </div>
        )}
      </div>

      {/* Est. available boxes range filter */}
      {(() => {
        const isActive = minBoxesFilter.min !== '' || minBoxesFilter.max !== '';
        return (
          <div className="sp-filter-wrapper" ref={boxesRef}>
            <button
              className={`sp-filter-btn${isActive ? ' active' : ''}`}
              onClick={() => { setBoxesOpen(o => !o); setBrandOpen(false); setNicheOpen(false); setTypeOpen(false); }}
            >
              <i className="fas fa-boxes"></i>
              Boxes
              {isActive && <span className="sp-filter-badge">
                {minBoxesFilter.min !== '' && minBoxesFilter.max !== '' ? '2' :  '1'}
              </span>}
              <i className={`fas fa-chevron-${boxesOpen ? 'up' : 'down'} sp-chevron`}></i>
            </button>
            {boxesOpen && (
              <div className="sp-dropdown sp-dropdown-boxes">
                <div className="sp-dropdown-header">Est. Available Boxes</div>
                <div className="sp-dropdown-range">
                  <div className="sp-dropdown-range-row">
                    <span className="sp-dropdown-range-label">From</span>
                    <input
                      type="number"
                      className="sp-dropdown-range-input"
                      min="0"
                      placeholder="0"
                      value={minBoxesFilter.min}
                      onChange={e => setMinBoxesFilter(prev => ({ ...prev, min: e.target.value }))}
                    />
                  </div>
                  <div className="sp-dropdown-range-row">
                    <span className="sp-dropdown-range-label">To</span>
                    <input
                      type="number"
                      className="sp-dropdown-range-input"
                      min="0"
                      placeholder="∞"
                      value={minBoxesFilter.max}
                      onChange={e => setMinBoxesFilter(prev => ({ ...prev, max: e.target.value }))}
                    />
                  </div>
                </div>
                {isActive && (
                  <button className="sp-dropdown-clear" onClick={() => setMinBoxesFilter({ min: '', max: '' })}>
                    Clear
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* Microgreens pack size filter */}
      <select
        className={`sp-size-select${sizeFilter !== '' ? ' active' : ''}`}
        value={sizeFilter}
        onChange={e => setSizeFilter(e.target.value)}
        title="Filter microgreens by pack size (oz)"
      >
        <option value="">Size: All</option>
        {packSizes.map(s => <option key={s} value={s}>{s} oz</option>)}
      </select>

      {/* Shipment-only toggle */}
      <button
        className={`sp-filter-btn${showShipmentOnly ? ' active' : ''}`}
        onClick={() => setShowShipmentOnly(v => !v)}
        title="Show only products with planned box quantities"
      >
        <i className="fas fa-truck"></i>
        To ship
      </button>

      {/* OOS-only toggle */}
      <button
        className={`sp-filter-btn${showOosOnly ? ' active' : ''}`}
        onClick={() => setShowOosOnly && setShowOosOnly(v => !v)}
        title="Show only out-of-stock products (no available units on the marketplace)"
      >
        <i className="fas fa-exclamation-circle"></i>
        OOS
      </button>

      {/* Results info */}
      <div className="sp-results-info">
        <span className="sp-results-count">{filteredCount} of {totalCount} products</span>
        {hasActiveFilters && (
          <button className="sp-clear-all" onClick={clearFilters}>
            <i className="fas fa-times-circle"></i> Clear filters
          </button>
        )}
      </div>

      <div className="sp-toolbar-divider" />

      {/* Stock upload actions — buttons render only when a handler is passed
          (Amazon FBA/AWD uploads hidden 31.07.2026: stock syncs from SP-API) */}
      <div className="sp-actions">
        {onOpenFbaPopup && (
          <button className="sp-action-btn" onClick={onOpenFbaPopup} title={`Upload ${stockButtonLabel} data`}>
            <i className="fas fa-upload"></i>
            {stockButtonLabel}
            {fbaUpdatedAt && <span className="sp-action-updated">{fmtTs(fbaUpdatedAt)}</span>}
          </button>
        )}
        {showAwd && onOpenAwdPopup && (
          <button className="sp-action-btn" onClick={onOpenAwdPopup} title="Upload AWD Stock data">
            <i className="fas fa-upload"></i>
            AWD Stock
            {awdUpdatedAt && <span className="sp-action-updated">{fmtTs(awdUpdatedAt)}</span>}
          </button>
        )}
        {showExport && (
          <button className="sp-action-btn sp-action-btn--export" onClick={onExportAmazonUpload} title="Export Amazon FBA shipment upload file">
            <i className="fas fa-file-download"></i>
            Amazon Upload
          </button>
        )}
        {showSkuMapping && (
          <button className="sp-action-btn sp-action-btn--settings" onClick={onOpenSkuMapping} title="SKU Mapping & Box Settings">
            <i className="fas fa-sliders-h"></i>
            SKU Settings
          </button>
        )}
      </div>

    </div>
  );
};

export default ShipmentsToolbar;
